/**
 * Which organization's bill custom fields a saved-view column catalog offers.
 *
 * Definitions are ORG-scoped while values are WORKSPACE-scoped, so the org to
 * ask is the one that owns the workspaces in scope. Both saved-view surfaces
 * used `organizations[0]` instead — and GET /organizations/user orders by name,
 * so that is "the alphabetically first org this user belongs to". For a
 * single-org user the two agree; for a user who also belongs to an
 * alphabetically earlier org they do not, and the Workspace columns list comes
 * back with Position and Notes only. CustomFieldEditModal already resolves the
 * org from the workspace's owner_org_id (custom-fields/CustomFieldEditModal.tsx);
 * this does the same for the catalog.
 *
 * A view may span workspaces in more than one org, so the catalog is the union
 * of those orgs' definitions rather than any single org's.
 */

import api from '../lib/api';
import { getCustomFieldDefinitions } from './customFieldsApi';
import type { CustomFieldDefinition } from '../types/custom-fields';

/** Owner org of one workspace, or null when it cannot be resolved. */
export async function ownerOrgIdForWorkspace(workspaceId: string): Promise<string | null> {
  const response = await api.get(`/workspaces/${workspaceId}`);
  // GET /workspaces/:id answers {success, data:{...workspace}}; tolerate a bare row.
  return response.data?.data?.owner_org_id ?? response.data?.owner_org_id ?? null;
}

/**
 * Active bill definitions across the given orgs, deduped by definition id and
 * kept in the order the orgs were given. One org failing (a membership that no
 * longer resolves, say) costs that org's columns, not the whole catalog.
 */
export async function fetchBillCustomFieldDefs(
  orgIds: readonly string[]
): Promise<CustomFieldDefinition[]> {
  const unique = Array.from(new Set(orgIds.filter(Boolean)));
  const perOrg = await Promise.all(
    unique.map((id) =>
      getCustomFieldDefinitions(id, 'bill', true).catch(() => [] as CustomFieldDefinition[])
    )
  );

  const seen = new Set<string>();
  const merged: CustomFieldDefinition[] = [];
  for (const defs of perOrg) {
    for (const def of defs ?? []) {
      if (seen.has(def.id)) continue;
      seen.add(def.id);
      merged.push(def);
    }
  }
  return merged;
}
